import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

import AppLayout from '@/components/blocks/layout/app-layout';
import Centered from '@/components/blocks/layout/centered';
import type { AuthContextProps } from '@/providers/auth-provider';
import CreateScenario from '@/routes/(app)/(scenarios)/(create)/create-scenario';
import SimulationPage from '@/routes/(app)/(scenarios)/(simulation)/simulation';
import ScenarioPage from '@/routes/(app)/(scenarios)/scenario';
import ScenariosPage from '@/routes/(app)/(scenarios)/scenarios';
import Callback from '@/routes/(auth)/callback';
import Login from '@/routes/(auth)/login';
import Root from '@/routes/root';

interface RouterContext {
  auth?: AuthContextProps;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: '/app/scenarios' });
    } else {
      throw redirect({ to: '/auth/login' });
    }
  },
});

const authRoute = createRoute({
  path: 'auth',
  getParentRoute: () => rootRoute,
  component: () => (
    <Centered>
      <Outlet />
    </Centered>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'login',
  validateSearch: z.object({
    redirect: z.string().optional(),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({
        to: decodeURIComponent(
          search.redirect ?? encodeURIComponent('/app/scenarios')
        ),
      });
    }
  },
  component: Login,
});

const callbackRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'callback',
  validateSearch: z.object({
    token: z.string().optional(),
    error: z.string().optional(),
    success: z.boolean(),
  }),
  component: Callback,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.auth?.isAuthenticated) {
      throw redirect({
        to: '/auth/login',
        search: {
          redirect: encodeURIComponent(location.href),
        },
      });
    }
  },
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

const scenariosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'scenarios',
  component: ScenariosPage,
});

const createScenarioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'scenarios/create',
  component: CreateScenario,
});

const scenarioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'scenarios/$scenarioId',
  component: ScenarioPage,
});

const simulationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'scenarios/$scenarioId/simulations/$simulationId',
  component: SimulationPage,
});

export const routes = rootRoute.addChildren([
  indexRoute,
  authRoute.addChildren([loginRoute, callbackRoute]),
  appRoute.addChildren([
    scenariosRoute,
    createScenarioRoute,
    scenarioRoute,
    simulationRoute,
  ]),
]);
